import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Layout from '../../components/Layout';
import { getMyEnrollments, getCourses, getLiveClasses, getAllProgress } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { BookOpen, Video, GraduationCap, Calendar, ChevronRight, Clock, Users, TrendingUp, Film } from 'lucide-react';

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [progressData, setProgressData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [enrollmentsData, coursesData, classesData] = await Promise.all([
          getMyEnrollments(),
          getCourses(),
          getLiveClasses()
        ]);
        setEnrollments(enrollmentsData);
        
        // Filter courses not enrolled
        const enrolledIds = enrollmentsData.map(e => e.course_id);
        setAvailableCourses(coursesData.filter(c => !enrolledIds.includes(c.id)).slice(0, 3));
        
        // Filter upcoming classes from enrolled courses
        const enrolledClassIds = enrollmentsData.map(e => e.course_id);
        const now = new Date();
        setUpcomingClasses(
          classesData
            .filter(c => enrolledClassIds.includes(c.course_id) && new Date(c.scheduled_at) > now)
            .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))
            .slice(0, 3)
        );

        // Get progress data if enrolled in courses
        if (enrollmentsData.length > 0) {
          try {
            const progress = await getAllProgress();
            setProgressData(progress);
          } catch (err) {
            console.log('No progress data yet');
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getProgressForCourse = (courseId) => {
    return progressData.find(p => p.course_id === courseId);
  };

  const totalProgress = progressData.length > 0 
    ? Math.round(progressData.reduce((sum, p) => sum + p.progress_percent, 0) / progressData.length)
    : 0;

  const totalCompleted = progressData.reduce((sum, p) => sum + p.completed_items, 0);
  const totalItems = progressData.reduce((sum, p) => sum + p.total_items, 0);

  if (loading) {
    return (
      <Layout>
        <div className="p-6 md:p-8 flex items-center justify-center min-h-[60vh]">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="student-dashboard">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-medium text-[#1A1A1A]">
            ¡Hola, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-[#737373] mt-2">Bienvenido a tu panel de estudiante</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">Mis Cursos</p>
                  <p className="text-3xl font-bold text-[#1A1A1A]">{enrollments.length}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-[#8B1538]/10 flex items-center justify-center">
                  <GraduationCap className="h-6 w-6 text-[#8B1538]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">Clases Vistas</p>
                  <p className="text-3xl font-bold text-[#1A1A1A]">{totalCompleted}/{totalItems}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-[#2D6A4F]/10 flex items-center justify-center">
                  <Film className="h-6 w-6 text-[#2D6A4F]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">Progreso General</p>
                  <p className="text-3xl font-bold text-[#1A1A1A]">{totalProgress}%</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-[#D9B48F]/30 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-[#B8860B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border border-[#E5DFD3] shadow-sm stats-card">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#737373]">Clases Próximas</p>
                  <p className="text-3xl font-bold text-[#1A1A1A]">{upcomingClasses.length}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-[#8B1538]/10 flex items-center justify-center">
                  <Video className="h-6 w-6 text-[#8B1538]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Course Progress */}
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl text-[#1A1A1A]">Mi Progreso</CardTitle>
              <TrendingUp className="h-5 w-5 text-[#8B1538]" />
            </CardHeader>
            <CardContent>
              {enrollments.length > 0 ? (
                <div className="space-y-6">
                  {enrollments.map((enrollment) => {
                    const progress = getProgressForCourse(enrollment.course_id);
                    const percent = progress?.progress_percent || 0;
                    return (
                      <div 
                        key={enrollment.id}
                        data-testid={`progress-${enrollment.course_id}`}
                        className="cursor-pointer hover:bg-[#F5F2EA] p-3 rounded-md -mx-3 transition-colors"
                        onClick={() => navigate(`/student/course/${enrollment.course_id}`)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-[#1A1A1A]">{enrollment.course_title}</h4>
                          <span className="text-sm font-semibold text-[#8B1538]">{percent}%</span>
                        </div>
                        <Progress value={percent} className="h-2" />
                        <div className="flex items-center justify-between mt-2 text-xs text-[#737373]">
                          <span>{progress?.completed_items || 0} de {progress?.total_items || 0} clases completadas</span>
                          {progress?.last_activity && (
                            <span>Última actividad: {new Date(progress.last_activity).toLocaleDateString('es-ES')}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" />
                  <p className="text-[#737373] mb-4">Inscríbete en un curso para ver tu progreso</p>
                  <Button
                    onClick={() => navigate('/student/courses')}
                    className="bg-[#8B1538] hover:bg-[#70102B] text-white"
                  >
                    Explorar Cursos
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Classes */}
          <Card className="bg-white border border-[#E5DFD3] shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-xl text-[#1A1A1A]">Próximas Clases</CardTitle>
              <Video className="h-5 w-5 text-[#8B1538]" />
            </CardHeader>
            <CardContent>
              {upcomingClasses.length > 0 ? (
                <div className="space-y-4">
                  {upcomingClasses.map((liveClass) => (
                    <div 
                      key={liveClass.id}
                      data-testid={`upcoming-class-${liveClass.id}`}
                      className="flex items-center justify-between p-4 bg-[#F5F2EA] rounded-md"
                    >
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-[#8B1538]/10 flex items-center justify-center">
                          <Video className="h-5 w-5 text-[#8B1538]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1A1A1A]">{liveClass.title}</p>
                          <div className="flex items-center gap-2 text-sm text-[#737373]">
                            <Calendar className="h-3.5 w-3.5" />
                            <span>{new Date(liveClass.scheduled_at).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            <Clock className="h-3.5 w-3.5 ml-2" />
                            <span>{new Date(liveClass.scheduled_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        data-testid={`join-class-btn-${liveClass.id}`}
                        size="sm"
                        onClick={() => navigate(`/student/class/${liveClass.id}`)}
                        className="bg-[#8B1538] hover:bg-[#70102B] text-white"
                      >
                        Unirse
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="h-12 w-12 mx-auto text-[#D9B48F] mb-3" />
                  <p className="text-[#737373]">No hay clases programadas</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Available Courses */}
        {availableCourses.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-xl text-[#1A1A1A]">Módulos Disponibles</h2>
              <Link to="/student/courses">
                <Button variant="ghost" size="sm" className="text-[#8B1538] hover:text-[#70102B]">
                  Ver todos <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {availableCourses.map((course) => (
                <Card 
                  key={course.id}
                  data-testid={`available-course-${course.id}`}
                  className={`bg-white border shadow-sm course-card overflow-hidden cursor-pointer ${course.is_featured ? 'border-[#8B1538] border-2' : 'border-[#E5DFD3]'}`}
                  onClick={() => navigate(`/student/course/${course.id}`)}
                >
                  {course.is_featured && (
                    <div className="bg-[#8B1538] text-white text-center py-1.5 text-xs font-medium">
                      Curso Principal
                    </div>
                  )}
                  <div className="h-32 bg-gradient-to-br from-[#8B1538] to-[#70102B] flex items-center justify-center">
                    <BookOpen className="h-10 w-10 text-white/50" />
                  </div>
                  <CardContent className="pt-4">
                    <h3 className="font-heading font-medium text-[#1A1A1A] mb-2">{course.title}</h3>
                    <p className="text-sm text-[#737373] line-clamp-2 mb-4">{course.description?.split('\n')[0]}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-sm text-[#737373]">
                        <Users className="h-4 w-4" />
                        <span>{course.enrolled_count}</span>
                      </div>
                      <span className="font-semibold text-[#8B1538]">${course.price_monthly.toLocaleString()}/mes</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
